import React, { useEffect, useRef, useState } from 'react';
import { Chip, cn, Slider, TooltipTriggerIcon } from '@crabs-ai/ds-react';
import TtsVoiceCard from '@/shared/components/dialog/TtsDialog/TtsVoiceCard';
import { useLocale, useTranslations } from 'next-intl';
import { useTtsDialog } from './TtsDialogContext';

interface TtsVoiceDetailProps {
  enableStyleDirective?: boolean;
}

export default function TtsVoiceDetail({
  enableStyleDirective = false,
}: TtsVoiceDetailProps) {
  const t = useTranslations();
  const locale = useLocale();

  // Context에서 상태 가져오기
  const {
    selectedVoice,
    selectedEmotion,
    setSelectedEmotion,
    tempo,
    setTempo,
    pitch,
    setPitch,
  } = useTtsDialog();

  if (!selectedVoice) {
    return null;
  }

  return (
    <>
      <div className={cn('rounded-xs bg-sys-style-inverse-light px-sm py-xs')}>
        <TtsVoiceCard
          key={selectedVoice.code}
          voice={selectedVoice}
          isDetail={true}
          enableStyleDirective={enableStyleDirective}
        />
      </div>
      <div className="flex flex-col gap-2xs rounded-xs bg-sys-style-inverse-light px-sm py-xs">
        <div className="flex items-center gap-2xs text-title-xsmall text-sys-cont-neutral-default">
          {t('Common.component.dialog.tts-dialog.select-emotion')}
          <TooltipTriggerIcon
            iconType="help"
            tooltipText={t(
              'Common.component.dialog.tts-dialog.select-emotion-tooltip',
            )}
          />
        </div>
        <div className="flex items-center gap-2xs">
          {selectedVoice.emotions.map((emotion) => (
            <Chip
              key={emotion.code}
              labelNode={locale === 'ko' ? emotion.nameKorean : emotion.name}
              selected={selectedEmotion === emotion}
              onClick={() => setSelectedEmotion(emotion)}
            />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2xs rounded-xs bg-sys-style-inverse-light px-sm py-xs">
        <div className="flex items-center gap-2xs text-title-xsmall text-sys-cont-neutral-default">
          {t('Common.component.dialog.tts-dialog.detail-option-setting')}
        </div>{' '}
        <div className="flex flex-col gap-4xs">
          <div className="flex items-center gap-4xs text-caption-small text-sys-cont-neutral-light">
            {t('Common.component.dialog.tts-dialog.tempo')}
            <TooltipTriggerIcon
              iconType="help"
              tooltipText={t(
                'Common.component.dialog.tts-dialog.tempo-tooltip',
              )}
            />
          </div>
          <div className="flex gap-2xs">
            <Slider
              value={[tempo]}
              min={selectedVoice.tempoMin}
              max={selectedVoice.tempoMax}
              onValueChange={(value) => setTempo(value[0] ?? 1.0)}
              step={0.1}
            />
            <ValueInputField
              value={tempo}
              min={selectedVoice.tempoMin}
              max={selectedVoice.tempoMax}
              step={0.1}
              onChange={(newTempo) => setTempo(newTempo)}
              suffix="x"
            />
          </div>
        </div>
        {selectedVoice.pitchMin != null && selectedVoice.pitchMax != null && (
          <div className="flex flex-col gap-4xs">
            <div className="flex items-center gap-4xs text-caption-small text-sys-cont-neutral-light">
              {locale === 'ko' ? '전반 음높이 (반음)' : 'Pitch (semitones)'}
              <TooltipTriggerIcon
                iconType="help"
                tooltipText={locale === 'ko' ? '생성된 음성을 이 브라우저에서 ±3반음까지 조절하며 재생 길이는 유지합니다.' : 'Adjust the generated audio locally by up to 3 semitones while keeping its duration.'}
              />
            </div>
            <div className="flex gap-2xs">
              <Slider
                value={[pitch]}
                min={selectedVoice.pitchMin}
                max={selectedVoice.pitchMax}
                onValueChange={(value) => setPitch(value[0] ?? 0)}
              />
              <ValueInputField
                value={pitch}
                min={selectedVoice.pitchMin}
                max={selectedVoice.pitchMax}
                step={1}
                onChange={(newPitch) => setPitch(newPitch)}
              />
            </div>
            {pitch !== 0 && <p className="text-caption-small text-sys-cont-neutral-light">
              {locale === 'ko' ? '이 음높이의 미리듣기 견본은 없습니다. 생성 후 실제 결과를 확인해 주세요.' : 'No preview sample at this pitch. Check the generated result.'}
            </p>}
          </div>
        )}
      </div>
    </>
  );
}

type ValueInputFieldProps = {
  value: number;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string | null;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>;

function ValueInputField({
  value,
  onChange,
  step = 1,
  min = 0,
  max = 100,
  suffix,
  className,
}: ValueInputFieldProps) {
  const ref = useRef<HTMLInputElement>(null);
  // 내부 state로 입력값 관리 (사용자가 자유롭게 입력할 수 있도록)
  const [internalValue, setInternalValue] = useState(String(value));

  // 외부 value prop이 변경되면 내부 state 동기화
  useEffect(() => {
    setInternalValue(String(value));
  }, [value]);

  // 입력 중에도 유효한 숫자면 즉시 반영
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setInternalValue(inputValue);

    // 유효한 숫자면 즉시 onChange 호출 (Slider와 즉시 동기화)
    const numValue = Number(inputValue);
    if (inputValue !== '' && !isNaN(numValue)) {
      onChange(numValue);
    }
  };

  // blur 시점에 min/max 범위로 자동 변환
  const handleBlur = () => {
    const numValue = Number(internalValue);

    // 빈 문자열이거나 유효하지 않은 숫자면 min 값으로 설정
    if (internalValue === '' || isNaN(numValue)) {
      const clampedValue = min;
      setInternalValue(String(clampedValue));
      onChange(clampedValue);
      return;
    }

    // min/max 범위로 clamp
    const clampedValue = Math.min(Math.max(numValue, min), max);
    if (clampedValue !== numValue) {
      setInternalValue(String(clampedValue));
      onChange(clampedValue);
    }
  };

  return (
    <div
      className={cn(
        'flex h-[36px] w-[64px] min-w-[64px] flex-1 items-center gap-4xs rounded-2xs bg-sys-style-neutral-lightest px-xs py-3xs text-sys-cont-neutral-default',
        className,
      )}
      onClick={(e) => {
        ref.current?.focus();
        e.stopPropagation();
      }}
    >
      <input
        ref={ref}
        type="number"
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        step={step}
        min={min}
        max={max}
        className={cn(
          'w-full bg-transparent text-left text-body-medium',
          '[appearance:textfield] outline-hidden [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        )}
      />
      {suffix && (
        <div className="flex w-[20px] max-w-[20px] items-center justify-center text-comp-accent-medium select-none">
          {suffix}
        </div>
      )}
    </div>
  );
}
